import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "../api/cliente";
import { chaves, usePainel } from "../api/consultas";
import { podeEditar, useAuth } from "../auth/AuthProvider";
import { useAvisos } from "../components/Avisos";
import { BadgeRegime, Carregando, ErroCarregamento, formatarData, Tabela } from "../components/ui";

export function Painel() {
  const { data, isLoading, error } = usePainel();
  const { usuario } = useAuth();
  const { avisar, avisarErro } = useAvisos();
  const queryClient = useQueryClient();

  const limpar = useMutation({
    mutationFn: () => api<{ mensagem: string }>("/api/orcamentos/limpar", { method: "POST", body: { escopo: "recentes" } }),
    onSuccess: (r) => {
      avisar(r.mensagem);
      queryClient.invalidateQueries({ queryKey: chaves.painel });
      queryClient.invalidateQueries({ queryKey: chaves.orcamentos });
    },
    onError: avisarErro,
  });

  if (isLoading) return <Carregando />;
  if (error || !data) return <ErroCarregamento erro={error} />;
  const { stats, familias, orcamentos } = data;

  return (
    <>
      <h1>Painel</h1>
      <p className="note">
        Preços e códigos importados exclusivamente de tabelas oficiais de saneamento básico (data-base Junho/2026 - R2).
        Veja{" "}
        <a href="https://www.embasa.ba.gov.br/fornecedores/semestres" target="_blank" rel="noreferrer">
          a fonte oficial
        </a>
        .
      </p>

      <div className="grid grid-3">
        <div className="card stat">
          <div className="num">{stats.itens}</div>
          <div className="label">Códigos oficiais</div>
        </div>
        <div className="card stat">
          <div className="num">
            {stats.variantes_importadas}/{stats.variantes_total}
          </div>
          <div className="label">Variantes de sindicato importadas</div>
        </div>
        <div className="card stat">
          <div className="num">{stats.municipios}</div>
          <div className="label">Municípios cadastrados</div>
        </div>
        <div className="card stat">
          <div className="num">{stats.orcamentos}</div>
          <div className="label">Orçamentos criados</div>
        </div>
      </div>

      <h2>Famílias de serviço</h2>
      <div className="card">
        <Tabela>
          <thead>
            <tr>
              <th>Família</th>
              <th>Código</th>
              <th className="num">Variantes importadas</th>
            </tr>
          </thead>
          <tbody>
            {familias.map((f) => (
              <tr key={f.codigo}>
                <td>{f.nome}</td>
                <td>
                  <span className="badge badge-blue">{f.codigo}</span>
                </td>
                <td className="num">
                  {f.importadas > 0 ? (
                    <span className="badge badge-green">
                      {f.importadas}/{f.total}
                    </span>
                  ) : (
                    <span className="badge badge-muted">0/{f.total || 0} - aguardando importação</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Tabela>
      </div>

      <h2 style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        Orçamentos recentes
        {orcamentos.length > 0 && podeEditar(usuario) && (
          <button
            type="button"
            className="btn-danger"
            style={{ fontSize: "0.8rem" }}
            disabled={limpar.isPending}
            onClick={() => {
              if (confirm("Remover os orçamentos recentes listados abaixo? Essa ação não pode ser desfeita.")) limpar.mutate();
            }}
          >
            Limpar orçamentos recentes
          </button>
        )}
      </h2>
      <div className="card">
        {orcamentos.length ? (
          <Tabela>
            <thead>
              <tr>
                <th>Nome</th>
                <th>Município</th>
                <th>Regime</th>
                <th>Criado em</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {orcamentos.map((o) => (
                <tr key={o.id}>
                  <td>{o.nome}</td>
                  <td>{o.municipio_nome || "—"}</td>
                  <td>
                    <BadgeRegime regime={o.regime} />
                  </td>
                  <td>{formatarData(o.criado_em)}</td>
                  <td>
                    <Link className="btn btn-ghost" to={`/orcamentos/${o.id}`}>
                      Abrir
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </Tabela>
        ) : (
          <p className="note">Nenhum orçamento ainda.</p>
        )}
        <Link className="btn btn-orange" to="/orcamentos/novo" style={{ marginTop: 12, display: "inline-block" }}>
          + Novo orçamento
        </Link>
      </div>
    </>
  );
}
